import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import Box from "@mui/material/Box";

import Toolbar from "@mui/material/Toolbar";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import AppBar from "@mui/material/AppBar";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Checkbox from "@mui/material/Checkbox";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

import { ButtonUI, KMKDialog } from "../../Common";
import DataPipelineService from "../../../service/dataPipelineService";
import { handlePipelineList } from "../../../store/actions/datapipeline";
import { showToast } from "../../../store/actions";
import { navScheduledJobs } from "../../kmk/AppData";
import { JOB_TYPES } from "./utils/JobData";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiDialogActions-root": {
      padding: "0 20px 10px 0",
    },
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  ActionBox: {
    display: "flex",
    justifyContent: "end",
    alignContent: "center",
    padding: "0 24px 16px 24px",
  },
}));

const CloneJob = (props) => {
  const { setOpenClone, pipelineId, handleSave, updateNodes, cloneNode, JobType } = props;

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const { dataPipelineList } = useMemo(() => dataPipeline, [dataPipeline]);

  const { Workspace_id, Job_id: stringId } = useParams();
  const { classes } = useStyles();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [dataPipelineId, setDataPipelineId] = useState(null);
  const [redirect, setRedirect] = useState(false);

  const textRef = useRef(null);

  const editJob_id = useMemo(() => cloneNode?.id || null, [cloneNode?.id]);

  const Job_id = useMemo(
    () => (stringId || editJob_id ? parseInt(stringId || editJob_id) : null),
    [editJob_id, stringId]
  );

  const redirectToSamePipeline = useMemo(
    () => parseInt(pipelineId) === parseInt(dataPipelineId),
    [dataPipelineId, pipelineId]
  );

  const handleClone = useCallback(
    async (e) => {
      setLoading(true);
      const isSaved = await handleSave(e, true);
      if (isSaved) {
        const datapipeline = new DataPipelineService();
        const { success, data, message } = await datapipeline.cloneJob(Workspace_id, pipelineId || dataPipelineId, {
          cloneDataPipeline_id: dataPipelineId,
          Job_id,
        });

        if (success) {
          dispatch(showToast({ message, type: "success" }));

          if (cloneNode && redirectToSamePipeline) {
            const randomNummber = Math.floor(Math.random() * 50) + 1;
            const updateData = {
              id: data.newJob_id + "",
              data: {
                ...cloneNode.data,
                label: data.name,
              },
              type: "jobNode",
              position: {
                x: cloneNode.xPos + randomNummber,
                y: cloneNode.yPos + randomNummber,
              },
            };

            updateNodes(updateData);
          } else if (data && redirect) {
            if (editJob_id) {
              navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/data-pipeline/${dataPipelineId}`);
            } else if (data.newJob_id) {
              let JobPath = JOB_TYPES.find((job) => job.type === JobType)?.path;
              navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/${JobPath}/${data.newJob_id || ""}`);
            }
          }
        }
      }
      setOpenClone(false);
      setLoading(false);
    },
    [
      JobType,
      Job_id,
      Workspace_id,
      cloneNode,
      dataPipelineId,
      dispatch,
      editJob_id,
      handleSave,
      navigate,
      pipelineId,
      redirect,
      redirectToSamePipeline,
      setOpenClone,
      updateNodes,
    ]
  );

  useLayoutEffect(() => {
    const fetchData = async () => {
      if (!dataPipelineList) {
        const getDataPipelineList = async () => {
          const pipeline = new DataPipelineService();
          const { success, data } = await pipeline.getDataPipelineList(Workspace_id);
          dispatch(handlePipelineList(success && data?.dataPipelineList?.length ? data.dataPipelineList : []));
        };
        getDataPipelineList();
      }

      if (pipelineId) {
        setDataPipelineId(pipelineId);
      } else {
        const DataPipeline_id = dataPipelineList?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id;
        if (DataPipeline_id) setDataPipelineId(DataPipeline_id);
      }
    };

    fetchData();
  }, [Workspace_id, dataPipelineList, dispatch, pipelineId]);

  return (
    <KMKDialog className={classes.dialogPaper} open={true} maxWidth="md" onClose={() => setOpenClone(false)}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>Clone Job</Toolbar>
        <IconButton
          aria-label="close"
          onClick={() => setOpenClone(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>

      <Box sx={{ width: "100%" }} className={classes.container}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>

        <DialogContent style={{ gap: "20px" }}>
          <FormControl sx={{ width: { xs: "100%" } }} required>
            <InputLabel>Pipeline</InputLabel>
            <Select
              value={dataPipelineId}
              onChange={(e) => setDataPipelineId(e.target.value)}
              label="Pipeline"
              MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
              renderValue={(selected) => {
                const selectedPipeline = (dataPipelineList || []).find((p) => p.DataPipeline_id === selected);
                const isOverflow = textRef.current?.scrollWidth > textRef.current?.clientWidth || false;

                return (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Tooltip placement="bottom" title={selectedPipeline?.Name} arrow disableHoverListener={!isOverflow}>
                      <Typography
                        variant="body1"
                        ref={textRef}
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}
                      >
                        {selectedPipeline?.Name || "Select a pipeline"}
                      </Typography>
                    </Tooltip>
                    {selectedPipeline?.IsDefault === 1 && (
                      <Tooltip placement="right" title="Default Pipeline" arrow>
                        <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                      </Tooltip>
                    )}
                  </Stack>
                );
              }}
            >
              {(dataPipelineList || []).map((p) => (
                <MenuItem
                  key={p.DataPipeline_id}
                  value={p.DataPipeline_id}
                  style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all" }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                      {p.Name}
                    </Typography>
                    {p.IsDefault === 1 && (
                      <Tooltip placement="right" title="Default Pipeline" arrow>
                        <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                      </Tooltip>
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ width: "100%", mt: "8px" }}>
            <FormControlLabel
              value={redirect}
              control={<Checkbox checked={redirect} />}
              label={`Navigate to the target ${editJob_id ? "pipeline" : "job"} after cloning.`}
              labelPlacement="end"
              onChange={(e) => setRedirect((prev) => !prev)}
              sx={{
                userSelect: "none",
                "& .MuiFormControlLabel-label": {
                  userSelect: "none",
                },
              }}
            />
          </Box>
        </DialogContent>
      </Box>

      <Box className={classes.ActionBox}>
        <ButtonUI variant="contained" color="secondary" onClick={() => setOpenClone(false)}>
          Cancel
        </ButtonUI>
        <ButtonUI
          data-id="addWSSave"
          disabled={!dataPipelineId || !dataPipelineList}
          variant="contained"
          color="success"
          onClick={handleClone}
          style={{ marginLeft: 15 }}
        >
          Save & Clone
        </ButtonUI>
      </Box>
    </KMKDialog>
  );
};

export default CloneJob;
